import { ErrorResponse } from 'types/form';
import { ApiResources, createErrorResponse, getContactData, getRequestHeaders, getUrl, Methods, processResponse } from './utils';

export enum EmploymentType {
  FullTime = 1,
  PartTime,
  Casual,
  SelfEmployed,
  Temp,
  Contract,
  Retired,
  Student,
  Military,
  Unemployed,
  Disabled,
  Other,
}

const employmentTypeMap: Record<string, EmploymentType> = {
  FullTime: EmploymentType.FullTime,
  'Self Employed': EmploymentType.SelfEmployed,
  PartTime: EmploymentType.PartTime,
  Contract: EmploymentType.Contract,
  Retired: EmploymentType.Retired,
  Student: EmploymentType.Student,
  Military: EmploymentType.Military,
  Unemployed: EmploymentType.Unemployed,
  Disabled: EmploymentType.Disabled,
  Other: EmploymentType.Other,
};

export interface IEmploymentDetailsPayload {
  monthsAtJob: number;
  jobTitle: string;
  employmentType: string;
  companyName: string;
  monthlyIncomeAmount: number;
  isCurrent: boolean;
  isPrimary: boolean;
  streetLine1: string;
  streetLine2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  startDate: string;
  endDate: string;
}

export interface IOtherIncomeDetailsPayload {
  incomeType: string;
  monthlyAmount: number;
  isCurrent: boolean;
  monthsAtJob: number;
  startDate?: string;
  endDate?: string;
}

export interface IUpdateEmploymentResponse {
  contactId: string;
  success: boolean;
  message: string;
}

export type IOtherIncomeDetailsResponse = IUpdateEmploymentResponse;

export const updateEmploymentDetails = async (
  employmentDetailsRequest: IEmploymentDetailsPayload[]
): Promise<IUpdateEmploymentResponse | ErrorResponse> => {
  const contactData = getContactData();
  const formattedEmploymentDetails = employmentDetailsRequest.map((employment) => ({
    ...employment,
    employmentType: employmentTypeMap[employment.employmentType] ?? EmploymentType.Other,
  }));
  if (contactData.contactId) {
    const endpoint = `${getUrl(ApiResources.contacts)}/${contactData.contactId}/${ApiResources.employment}`;
    const request = {
      method: Methods.patch,
      headers: await getRequestHeaders(),
      body: JSON.stringify(formattedEmploymentDetails),
    };
    try {
      const response = await fetch(endpoint, request);
      return processResponse(response);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
    }
  } else {
    return createErrorResponse('', 'Error while updating employment details');
  }
};

export const updateOtherIncomeDetails = async (
  otherIncomeDetailsRequest: IOtherIncomeDetailsPayload[]
): Promise<IOtherIncomeDetailsResponse | ErrorResponse> => {
  const contactData = getContactData();
  if (contactData.contactId) {
    const endpoint = `${getUrl(ApiResources.contacts)}/${contactData.contactId}/${ApiResources.otherIncome}`;
    const request = {
      method: Methods.patch,
      headers: await getRequestHeaders(),
      body: JSON.stringify(otherIncomeDetailsRequest),
    };
    try {
      const response = await fetch(endpoint, request);
      return processResponse(response);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
    }
  } else {
    return createErrorResponse('', 'Error while updating other income details');
  }
};
